import React from 'react';
import {  
    Box,
    Button,
    Container,
    styled,
    Typography,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Details = () => {
    const Section = styled(Box)(({ theme }) => ({
        padding: theme.spacing(10, 0),
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
    }));

    const PageTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(2.5rem, 5vw, 4rem)',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    }));

    const Subtitle = styled(Typography)(({ theme }) => ({
        textAlign: 'center',
        color: '#64748b',
        fontSize: '1.2rem',
        marginBottom: theme.spacing(6),
        maxWidth: '700px',
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.8,
    }));

    const ContactCard = styled(Card)(({ theme }) => ({
        height: '100%',
        borderRadius: '20px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: '#fff',
        '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 60px rgba(255, 107, 53, 0.15)',
            borderColor: '#FF6B35',
        },
    }));

    const IconWrapper = styled(Box)(({ theme }) => ({
        width: '70px',
        height: '70px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        '& svg': {
            fontSize: '2rem',
            color: '#fff',
        },
    }));

    const ContactTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#1e293b',
        marginBottom: theme.spacing(1),
    }));

    const ContactInfo = styled(Typography)({
        fontSize: '1rem',
        color: '#64748b',
        lineHeight: 1.7,
    });

    const WhatsAppButton = styled(Button)(({ theme }) => ({
        padding: '1.2rem 3rem',
        fontSize: '1.1rem',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '50px',
        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
        color: '#fff',
        boxShadow: '0 10px 30px rgba(37, 211, 102, 0.4)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        marginTop: theme.spacing(4),
        '&:hover': {
            background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
            transform: 'translateY(-3px)',
            boxShadow: '0 15px 40px rgba(37, 211, 102, 0.5)',
        },
        '& .MuiButton-startIcon': {
            fontSize: '1.5rem',
        },
    }));

    const handleWhatsAppClick = () => {
        window.open('https://wa.me/917639568608', '_blank');
    };

    const contactDetails = [
        {
            icon: <PhoneIcon />,
            title: 'Phone',
            info: '+91 76395 68608',
            link: 'tel:+917639568608',
        },
        {
            icon: <EmailIcon />,
            title: 'Email',
            info: 'info@perumalcranes.com',
            link: 'mailto:info@perumalcranes.com',
        },
        {
            icon: <LocationOnIcon />,
            title: 'Location',
            info: 'Coimbatore, Tamil Nadu, India',
            link: null,
        },
        {
            icon: <AccessTimeIcon />,
            title: 'Working Hours',
            info: '24/7 Available',
            link: null,
        },
    ];

    return (
        <Section component="section">
            <Container maxWidth="lg">
                <PageTitle variant="h1">
                    Get In Touch With Us
                </PageTitle>
                
                <Subtitle>
                    If you are interested in our services, please contact us. We will promptly reach out 
                    to you on WhatsApp to discuss and fulfill your requirements.
                </Subtitle>

                <Grid container spacing={4} sx={{ mb: 4 }}>
                    {contactDetails.map((detail, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <ContactCard>
                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                    <IconWrapper sx={{ mx: 'auto' }}>
                                        {detail.icon}
                                    </IconWrapper>
                                    <ContactTitle>
                                        {detail.title}
                                    </ContactTitle>
                                    <ContactInfo>
                                        {detail.link ? (
                                            <a 
                                                href={detail.link}
                                                style={{ 
                                                    color: '#64748b',
                                                    textDecoration: 'none',
                                                    transition: 'color 0.3s ease',
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#FF6B35'}
                                                onMouseLeave={(e) => e.target.style.color = '#64748b'}
                                            >
                                                {detail.info}
                                            </a>
                                        ) : (
                                            detail.info
                                        )}
                                    </ContactInfo>
                                </CardContent>
                            </ContactCard>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ textAlign: 'center' }}>
                    <WhatsAppButton
                        variant="contained"
                        size="large"
                        startIcon={<WhatsAppIcon />}
                        onClick={handleWhatsAppClick}
                    >
                        Chat on WhatsApp
                    </WhatsAppButton>
                </Box>
            </Container>
        </Section>
    );
};

export default Details;